import { useState } from 'react'
import { Copy, Check } from 'lucide-react'
import { copyToClipboard } from '../../lib/clipboard'
import { useUIStore } from '../../stores/uiStore'
import { useQueryStore } from '../../stores/queryStore'
import { useHistoryStore } from '../../stores/historyStore'
import { useAnalyticsStore } from '../../stores/analyticsStore'
import type { Platform } from '../../types'
import clsx from 'clsx'

interface CopyButtonProps {
  text: string
  platform: Platform
}

export default function CopyButton({ text, platform }: CopyButtonProps) {
  const [copied, setCopied] = useState(false)
  const { showToast } = useUIStore()
  const { currentHistoryId } = useQueryStore()
  const { markCopied } = useHistoryStore()
  const { trackCopy } = useAnalyticsStore()

  const handleCopy = async () => {
    const success = await copyToClipboard(text)

    if (success) {
      setCopied(true)
      showToast(`Copied ${platform} response to clipboard`, 'success')

      // Track the copy
      if (currentHistoryId) {
        markCopied(currentHistoryId, platform)
      }
      trackCopy(platform)

      // Reset after 2 seconds
      setTimeout(() => setCopied(false), 2000)
    } else {
      showToast('Failed to copy to clipboard', 'error')
    }
  }

  return (
    <button
      onClick={handleCopy}
      className={clsx(
        'btn transition-transform hover:scale-[1.02] active:scale-[0.98]',
        copied ? 'btn-primary' : 'btn-ghost'
      )}
      aria-label={copied ? 'Copied!' : `Copy ${platform} response`}
    >
      {copied ? (
        <>
          <Check className="w-4 h-4" />
          Copied!
        </>
      ) : (
        <>
          <Copy className="w-4 h-4" />
          Copy
        </>
      )}
    </button>
  )
}
